import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Card, Space, Segmented, Typography } from "antd";
import { ApiOutlined, SecurityScanOutlined } from "@ant-design/icons";
import OIDCRegForm from "../protocols/OIDCRegForm";
import SAMLRegForm from "../protocols/SAMLRegForm";
import CategoryOption from "../CategoryOption";
import { getAllProviders } from "../../selectors";
import { getRegistrationProviders } from "../../actions";
import { RegistrationProtocols } from "../../models/registrationProtocols";

const { Title } = Typography;

const RegistrationFormWrapper = ({
  application,
  providers,
  handleSubmit,
  getRegistrationProviders,
  isEdit,
  selectedRegistration,
}) => {
  const [mode, setMode] = useState(RegistrationProtocols.OIDC);

  useEffect(() => {
    getRegistrationProviders?.();
  }, [getRegistrationProviders]);

  useEffect(() => {
    if (isEdit && selectedRegistration?.providerIdentifier) {
      setMode(selectedRegistration.providerIdentifier);
    }
  }, [isEdit, selectedRegistration]);

  const handleAddRegistration = useCallback(
    (providerIdentifier, data) => {
      if (!application?.id) return;
      const provider = providers.find(
        (el) => el.authenticationProviderIdentifier === providerIdentifier,
      );
      if (!provider?.id) return;
      handleSubmit(application.id, provider.id, data);
    },
    [application?.id, providers, handleSubmit],
  );

  const handleAddOIDCRegistration = useCallback(
    (data) => {
      const uniqueRedirects = Array.from(new Set(data.redirectUris));
      handleAddRegistration(RegistrationProtocols.OIDC, {
        ...data,
        description: application?.description,
        name: application?.displayName,
        redirectUris: uniqueRedirects,
      });
    },
    [handleAddRegistration, application?.description, application?.displayName],
  );

  const handleAddSAMLRegistration = useCallback(
    (data) => {
      handleAddRegistration(RegistrationProtocols.SAML, {
        ...data,
        description: application?.description,
        name: application?.displayName,
      });
    },
    [handleAddRegistration, application?.description, application?.displayName],
  );

  const segmentedOptions = useMemo(
    () => [
      {
        value: RegistrationProtocols.SAML,
        label: (
          <CategoryOption
            icon={<SecurityScanOutlined />}
            title="SAML"
            subtitle="Security Assertion Markup Language"
          />
        ),
      },
      {
        value: RegistrationProtocols.OIDC,
        label: (
          <CategoryOption
            icon={<ApiOutlined />}
            title="OIDC"
            subtitle="OpenID Connect"
          />
        ),
      },
    ],
    [],
  );

  if (!providers?.length) return null;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {!isEdit && (
        <div>
          <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
            Which protocol does your application use for authentication?
          </Title>
          <Segmented
            id="registrationProtocol"
            block
            value={mode}
            onChange={(val) => setMode(val)}
            options={segmentedOptions}
          />
        </div>
      )}

      <Card>
        {mode === RegistrationProtocols.OIDC && (
          <OIDCRegForm submitFunction={handleAddOIDCRegistration} />
        )}

        {mode === RegistrationProtocols.SAML && (
          <SAMLRegForm submitFunction={handleAddSAMLRegistration} />
        )}
      </Card>
    </Space>
  );
};

RegistrationFormWrapper.propTypes = {
  application: PropTypes.object,
  providers: PropTypes.array,
  handleSubmit: PropTypes.func,
  getRegistrationProviders: PropTypes.func,
  isEdit: PropTypes.bool,
  selectedRegistration: PropTypes.object,
};

export default connect(
  (state) => ({
    providers: getAllProviders(state),
    selectedRegistration: state.selectedRegistration,
  }),
  { getRegistrationProviders },
)(RegistrationFormWrapper);
